import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import request from '../../../app/api/requests';
import { fetchSubscription } from './fetch-subscription';
import type { PaymentSyncResult, SubscriptionPayment, SubscriptionRootState } from './types';
import { isValidPaymentOrderId } from './pending-payment';

const statuses: SubscriptionPayment['status'][] = ['Creating', 'Pending', 'Succeeded', 'Canceled', 'NeedsReview'];

// The caller supplies the original order ID, never a client-side "paid" flag.
// This single check is also reusable by the later payment-return polling flow.
export const syncSubscriptionAfterPayment = createAsyncThunk<
    PaymentSyncResult,
    string,
    { state: SubscriptionRootState; rejectValue: string; pendingMeta: { userId: string }; rejectedMeta: { retryable: boolean } }
>(
    'subscription/syncAfterPayment',
    async (orderId, { dispatch, getState, requestId, signal, rejectWithValue }) => {
        if (!isValidPaymentOrderId(orderId)) {
            return rejectWithValue('Некорректный номер заказа.', { retryable: false });
        }
        const userId = getState().userAuthSettings.userId;
        const isCurrentSession = () => {
            const state = getState();
            return !signal.aborted && state.userAuthSettings.isAuth
                && state.userAuthSettings.userId === userId
                && state.subscription.paymentSync.currentRequestId === requestId;
        };
        try {
            const { data: payment } = await request.get<SubscriptionPayment>(
                `/Subscriptions/payments/${orderId.toLowerCase()}`, { signal, timeout: 15000 },
            );
            if (!isCurrentSession()) return rejectWithValue('Сессия пользователя изменилась.', { retryable: false });
            if (!payment || typeof payment.orderId !== 'string'
                || payment.orderId.toLowerCase() !== orderId.toLowerCase()
                || !statuses.includes(payment.status)) {
                return rejectWithValue('Получены некорректные данные платежа.', { retryable: false });
            }
            if (payment.status !== 'Succeeded' || !payment.appliedAt) {
                return { payment, subscriptionUpdated: false };
            }
            if (typeof payment.appliedAt !== 'string' || !Number.isFinite(Date.parse(payment.appliedAt))) {
                return rejectWithValue('Получены некорректные данные платежа.', { retryable: false });
            }

            // A request started before payment may still be returning Basic/Trial.
            // The new request supersedes it; the slice ignores its late response.
            const refreshed = await dispatch(fetchSubscription({ force: true }, { signal }));
            if (!isCurrentSession()) return rejectWithValue('Сессия пользователя изменилась.', { retryable: false });
            if (!fetchSubscription.fulfilled.match(refreshed)) {
                return rejectWithValue('Оплата подтверждена, но не удалось обновить подписку. Повторите обновление.', { retryable: true });
            }
            return { payment, subscriptionUpdated: true };
        } catch (error) {
            const status = axios.isAxiosError(error) ? error.response?.status : undefined;
            return rejectWithValue('Не удалось проверить оплату. Повторите попытку.', {
                retryable: !status || ![400, 401, 403, 404].includes(status),
            });
        }
    },
    {
        condition: (_, { getState }) => {
            const { userAuthSettings: user, subscription } = getState();
            return user.isAuth && !!user.userId && !subscription.paymentSync.currentRequestId;
        },
        getPendingMeta: (_, { getState }) => ({ userId: getState().userAuthSettings.userId }),
    },
);
